import { getRetryAfterMs } from 'pi-kiosk-shared';
import { readRequestId, setClientCorrelationId } from 'pi-kiosk-shared/logging';
import { setSentryCorrelationId } from 'pi-kiosk-shared/sentry';
import {
  ApiError,
  messageFromApiErrorEnvelope,
  type ApiErrorEnvelope,
} from '../../services/api/apiClient.types.js';
import { getEnvironmentConfig } from '../../config/environments.js';
import i18n from '../i18n/i18n.js';
import { captureAdminRateLimitBreadcrumb } from '../observability/sentry.js';
import { buildTenantApiBase, withTenantInPath } from '../tenant.js';
import {
  clearDenied,
  denyKeyForApiPath,
  entitlementDenyKindsFromEnvelopeDetails,
  isAdminMeRouteFamily,
  isDenied,
  markDenied,
  routeFamilyFromApiPath,
  shouldMarkApiDenied,
} from '../http/apiDenyCache.js';
import {
  buildRequestFailureCacheKey,
  isAbortError,
  markRequestFailure,
  shouldSkipCachedFailure,
} from '../http/requestFailureCache.js';
import {
  HttpStormLatchedError,
  stormAwareFetch,
} from '../http/stormAwareFetch.js';

export class AdminSwrCachedFailureError extends Error {
  public constructor(key: string) {
    super(`Skipping cached failure for ${key}`);
    this.name = 'AdminSwrCachedFailureError';
  }
}

function resolveTenantEndpoint(key: string): string {
  return key.startsWith('/api') ? withTenantInPath(key) : key;
}

function resolveGetUrl(key: string): string {
  const config = getEnvironmentConfig();
  const baseUrl = buildTenantApiBase(config.apiUrl);
  return `${baseUrl}${resolveTenantEndpoint(key)}`;
}

function retryAfterMsFromHttpResponse(response: Response): number | undefined {
  if (!response.headers.has('retry-after') && !response.headers.has('ratelimit-reset')) {
    return undefined;
  }
  return getRetryAfterMs({ response });
}

/**
 * G12 / G24 — credentials-first SWR GET fetcher (`credentials:'include'`).
 * Optional dual Bearer only when caller injects `getToken`; no default LS JWT probe.
 * G17 — stormAwareFetch arms latch + soft-stops while latched.
 */
export function createAdminSwrFetcher(
  getToken?: () => string | null
): (key: string) => Promise<unknown> {
  return async function adminSwrFetcher(key: string): Promise<unknown> {
    const tenantEndpoint = resolveTenantEndpoint(key);
    const denyKey = denyKeyForApiPath(tenantEndpoint, 'GET');
    if (isDenied(denyKey)) {
      throw new AdminSwrCachedFailureError(denyKey);
    }

    const cacheKey = buildRequestFailureCacheKey(['GET', key]);
    if (shouldSkipCachedFailure(cacheKey)) {
      throw new AdminSwrCachedFailureError(cacheKey);
    }

    const token = getToken?.() ?? null;
    const headers: Record<string, string> = {
      'X-Request-ID': crypto.randomUUID(),
      'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await stormAwareFetch(resolveGetUrl(key), {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (!response.ok) {
        // G4: logged-out `/admin/me` 401 must not poison requestFailureCache
        // (AuthProvider probes me while unauthenticated; post-login must retry).
        if (
          !(
            response.status === 401 &&
            isAdminMeRouteFamily(routeFamilyFromApiPath(tenantEndpoint))
          )
        ) {
          markRequestFailure(cacheKey, response.status);
        }
        const errorData = (await response.json().catch(() => ({
          success: false as const,
        }))) as ApiErrorEnvelope;
        const correlationId =
          readRequestId(response) ??
          (typeof errorData.requestId === 'string' ? errorData.requestId : undefined);
        if (correlationId !== undefined) {
          setClientCorrelationId(correlationId);
          setSentryCorrelationId(correlationId);
        }
        const code = typeof errorData.code === 'string' ? errorData.code : undefined;
        const entitlementDenyKinds = entitlementDenyKindsFromEnvelopeDetails(
          errorData.details,
        );
        if (
          shouldMarkApiDenied(
            response.status,
            code,
            'GET',
            routeFamilyFromApiPath(tenantEndpoint),
            entitlementDenyKinds,
          )
        ) {
          markDenied(denyKey);
        }
        const retryAfterMs = retryAfterMsFromHttpResponse(response);
        if (response.status === 429) {
          captureAdminRateLimitBreadcrumb({
            path: key,
            method: 'GET',
            retryAfterMs: retryAfterMs ?? getRetryAfterMs({ response }),
          });
        }
        // G2 — same pick path as apiClient.core throwFromFailedResponse
        throw new ApiError(
          response.status,
          errorData,
          messageFromApiErrorEnvelope(errorData, response.status),
          code,
          retryAfterMs,
          correlationId
        );
      }
      const responseId = readRequestId(response);
      if (responseId !== undefined) {
        setClientCorrelationId(responseId);
        setSentryCorrelationId(responseId);
      }
      // Spec §3.4 — success GET clears that route deny key.
      clearDenied(denyKey);
      return response.json() as Promise<unknown>;
    } catch (error) {
      if (error instanceof HttpStormLatchedError) {
        markRequestFailure(cacheKey, 429);
        throw new ApiError(
          429,
          { success: false, code: error.code },
          error.message,
          error.code,
          error.retryAfterMs,
        );
      }
      if (isAbortError(error)) {
        throw error;
      }
      if (!(error instanceof AdminSwrCachedFailureError) && !(error instanceof ApiError)) {
        markRequestFailure(cacheKey, 0);
      }
      throw error;
    }
  };
}
